import { LoadingOverlayComponent } from '../../shared/components/loading-overlay/loading-overlay.component';

import { ImageService } from '../../cache/image.service';
import { MapService } from '../../map/map.service';
import { RadarDtg, RadarService } from '../../radar/radar.service';

import { DatexPipe } from '../../shared/datex.pipe';

import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  inject,
  Injector,
  input,
  OnDestroy,
  OnInit,
  signal,
  untracked,
} from '@angular/core';

import {
  FeatureComponent,
  GeoJSONSourceComponent,
  LayerComponent,
  MapComponent as NgxMapboxMapComponent,
  RasterSourceComponent,
} from 'ngx-mapbox-gl';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';

import { environment } from '../../../environments/environment';

import { getRadarTileUrl } from '../../map/radar/radar-tile.utils';
import { fcstRunTypes } from '../../radar/constants/constants';
import { LngLatLike, Map as MapboxMap } from 'mapbox-gl';
import moment, { Moment } from 'moment';
import { catchError, map, Observable, of, Subscription, timer } from 'rxjs';
import { isNil } from 'src/utils/utils';

export const MODULES = [CommonModule];
export const COMPONENTS = [
  NgxMapboxMapComponent,
  RasterSourceComponent,
  LayerComponent,
  GeoJSONSourceComponent,
  FeatureComponent,
];

@Component({
  selector: 'app-dashboard-radar',
  templateUrl: './dashboard-radar.component.html',
  styleUrls: ['./dashboard-radar.component.scss'],
  providers: [ImageService],
  imports: [
    ...MODULES,
    ...COMPONENTS,
    LoadingOverlayComponent,
    DatexPipe,
    FaIconComponent,
  ],
})
export class DashboardRadarComponent implements OnInit, OnDestroy {
  private injector = inject(Injector);
  public mapService = inject(MapService);
  private radarService = inject(RadarService);

  settings = input<any>();
  radarDtg = input<Moment>();

  radarProjections = {
    de: [3.51175, 46.959255, 17.381118, 55.600819],
    ch: [3.51175, 46.959255, 17.381118, 55.600819],
    benelux: [0, 48.8833, 9, 55.9667],
    es: [0, 48.8833, 9, 55.9667],
    eu: [-14.993, 41.4678541, 20.4357948, 60.0106635],
    fr: [-9.965, 37.4602971, 17.5597105, 54.1840311],
  };
  radarPaintVisible = {
    'raster-opacity': 0.8,
    'raster-fade-duration': 0,
    'raster-opacity-transition': { delay: 0, duration: 0 },
  };
  // de/benelux/es/eu/fr/ch: +0.2 gegenüber den ursprünglichen Werten – die
  // Karte zeigt durch die größere Kartenhöhe (480px statt 430px,
  // Kartenbereich füllt jetzt vollständig aus statt auf 330px gedeckelt zu
  // sein) bei gleichem Zoom spürbar mehr Fläche, das gleicht den
  // "rausgezoomt"-Eindruck wieder aus.
  // city: nur ein kleines bisschen näher als der vorherige (ungenutzte)
  // Default von 6 – siehe ngOnInit().
  zoomLevelForRegion = {
    de: 4.2,
    benelux: 5.2,
    es: 4.2,
    eu: 3.2,
    fr: 4.2,
    ch: 5.2,
    city: 6.25,
  };
  mapboxAccessToken = environment.mapbox.accessToken;
  radarCirclePaint: any;
  radarZoomLevel = signal(6);
  radarCircleGeometry = signal<Array<any>>([]);

  radarAnimationFrames = signal<RadarDtg[]>([]);
  activeRadarFrameIndex = signal(0);
  isRadarAnimationPlaying = signal(true);

  // Matches RadarComponent's default forecast timerange, since the dashboard
  // card has no user-facing timerange setting of its own.
  private readonly forecastLookaheadMinutes = 120;

  private radarAnimationSubscription?: Subscription;
  private radarFramesSubscription?: Subscription;
  // Mapbox GL sizes its canvas once from the container's dimensions at
  // creation time and never re-measures it on its own; if the dashboard
  // card's layout settles to its final width after that (e.g. card-grid
  // reflow on load, a sidenav toggle), the canvas is left narrower than its
  // container. A ResizeObserver on the container catches any later resize.
  private mapResizeObserver?: ResizeObserver;

  constructor() {
    this.listenToRadarDtgForAnimationFrames();
  }

  ngOnInit(): void {
    const settings = this.settings();
    if (settings.zoomPoint.searchName === 'whole Area') {
      this.radarZoomLevel.set(this.zoomLevelForRegion[settings.region]);
    } else {
      // Auf einen konkreten Ort gezoomt: etwas näher heran als bei der
      // Gesamtregion-Ansicht, statt beim ungenutzten Default (6) zu bleiben.
      this.radarZoomLevel.set(this.zoomLevelForRegion['city']);
      this.paintCircles();
    }

    this.startRadarAnimation();
  }

  ngOnDestroy(): void {
    this.stopRadarAnimation();
    this.radarFramesSubscription?.unsubscribe();
    this.mapResizeObserver?.disconnect();
  }

  onMapCreate(map: MapboxMap): void {
    this.mapResizeObserver = new ResizeObserver(() => map.resize());
    this.mapResizeObserver.observe(map.getContainer());
  }

  get activeRadarFrame(): Moment {
    const frames = this.radarAnimationFrames();
    const frame = frames[this.activeRadarFrameIndex()];
    return frame ? moment.unix(frame.slider) : this.radarDtg();
  }

  get isActiveRadarFrameFcst(): boolean {
    const frames = this.radarAnimationFrames();
    return frames[this.activeRadarFrameIndex()]?.isFcst ?? false;
  }

  get activeRadarFramePositionPercent(): number {
    const frameCount = this.radarAnimationFrames().length;
    if (frameCount <= 1) {
      return 0;
    }
    return (this.activeRadarFrameIndex() / (frameCount - 1)) * 100;
  }

  getRadarTileUrl(dtg: RadarDtg): string {
    const region = this.settings()?.['region'];
    return getRadarTileUrl(dtg, region, false) ?? '';
  }

  // Verschiebt den angezeigten Kartenmittelpunkt um ein paar Pixel nach
  // Süden (der Slider verdeckt sonst mehr vom eigentlich interessanten
  // Bereich als der Himmel/nördliche Rand). Eine echte Verschiebung über
  // die Koordinate statt eines CSS-Zuschnitts, damit die Mapbox-Controls
  // (Logo) nicht mit abgeschnitten werden.
  private readonly centerShiftPixels = 11;

  getMapCenter(
    zoomPoint: { longitude: number; latitude: number },
    zoom: number
  ): [number, number] {
    return [
      zoomPoint.longitude,
      this.shiftLatitudeSouth(zoomPoint.latitude, zoom, this.centerShiftPixels),
    ];
  }

  private shiftLatitudeSouth(
    latitude: number,
    zoom: number,
    pixels: number
  ): number {
    // Standard-Web-Mercator-Pixelprojektion (256px-Kacheln, wie von Mapbox
    // intern verwendet): Breitengrad -> globale Pixel-Y-Position bei
    // gegebenem Zoom, dort den Pixel-Offset addieren, zurück in einen
    // Breitengrad umrechnen.
    const scale = 256 * Math.pow(2, zoom);
    const sinLat = Math.sin((latitude * Math.PI) / 180);
    const pixelY =
      (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * scale;
    const shiftedPixelY = pixelY + pixels;
    const n = Math.PI - (2 * Math.PI * shiftedPixelY) / scale;
    return (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
  }

  toggleRadarAnimation(event: MouseEvent): void {
    event.stopPropagation();

    if (this.isRadarAnimationPlaying()) {
      this.isRadarAnimationPlaying.set(false);
      this.stopRadarAnimation();
    } else {
      this.isRadarAnimationPlaying.set(true);
      this.startRadarAnimation();
    }
  }

  selectRadarFrame(index: number, event: Event): void {
    event.stopPropagation();
    event.preventDefault();

    const frames = this.radarAnimationFrames();
    if (index < 0 || index >= frames.length) {
      return;
    }

    this.isRadarAnimationPlaying.set(false);
    this.stopRadarAnimation();
    this.activeRadarFrameIndex.set(index);
  }

  getFrameTime(frame: RadarDtg): string {
    return moment.unix(frame.slider).format('HH:mm');
  }

  private startRadarAnimation(): void {
    this.stopRadarAnimation();

    this.radarAnimationSubscription = timer(0, 700).subscribe(() => {
      if (!this.isRadarAnimationPlaying()) {
        return;
      }

      const frames = this.radarAnimationFrames();
      if (frames.length <= 1) {
        return;
      }

      this.activeRadarFrameIndex.update(index => {
        return index >= frames.length - 1 ? 0 : index + 1;
      });
    });
  }

  private stopRadarAnimation(): void {
    this.radarAnimationSubscription?.unsubscribe();
    this.radarAnimationSubscription = undefined;
  }

  private listenToRadarDtgForAnimationFrames(): void {
    effect(
      () => {
        const radarDtg = this.radarDtg();
        const region = this.settings()?.['region'];

        untracked(() => {
          if (!radarDtg || !region) {
            return;
          }

          this.updateRadarAnimationFrames(radarDtg, region);
        });
      },
      { injector: this.injector }
    );
  }

  private updateRadarAnimationFrames(radarDtg: Moment, region: string): void {
    this.radarFramesSubscription?.unsubscribe();

    const pastFrames = this.buildPastRadarFrames(radarDtg, region);
    const pastTimestamps = new Set(pastFrames.map(frame => frame.slider));

    this.radarFramesSubscription = this.fetchForecastRadarFrames(
      radarDtg,
      region
    ).subscribe(fcstFrames => {
      // Obs wins for every timestep both runs share - the fcst run can
      // overlap with more than just the latest past frame, so skip any fcst
      // frame whose valid time matches one already built above.
      const dedupedFcstFrames = fcstFrames.filter(
        frame => !pastTimestamps.has(frame.slider)
      );

      this.setRadarAnimationFrames([...pastFrames, ...dedupedFcstFrames]);
    });
  }

  // Starts on the last non-forecast frame, matching RadarComponent's
  // setSliderOptions behavior.
  private setRadarAnimationFrames(frames: RadarDtg[]): void {
    this.radarAnimationFrames.set(frames);

    const fcstIndex = frames.findIndex(frame => frame.isFcst);
    const initialIndex = fcstIndex === -1 ? frames.length - 1 : fcstIndex - 1;

    this.activeRadarFrameIndex.set(Math.max(initialIndex, 0));
  }

  private buildPastRadarFrames(
    latestRadarDtg: Moment,
    region: string
  ): RadarDtg[] {
    const frameCount = 24;
    const minutesPerFrame = region === 'fr' || region === 'es' ? 15 : 5;

    return Array.from({ length: frameCount }, (_, index) => {
      const minutesBack = (frameCount - 1 - index) * minutesPerFrame;
      const dtg = latestRadarDtg.clone().subtract(minutesBack, 'minutes');

      return {
        mapServer: dtg.clone().utc().format('YYYYMMDDHHmm'),
        info: dtg.clone().format('dd DD.MM.YYYY HH:mm'),
        slider: dtg.unix(),
        visible: false,
        isFcst: false,
        isArchive: false,
        issue: 0,
      };
    });
  }

  private fetchForecastRadarFrames(
    latestRadarDtg: Moment,
    region: string
  ): Observable<RadarDtg[]> {
    const fcstType = fcstRunTypes[region];
    if (isNil(fcstType)) {
      return of([]);
    }

    return this.radarService.getLatestFcstRun(fcstType).pipe(
      map(fcstData => {
        if (!fcstData?.length) {
          return [];
        }

        const currentFcstRun = fcstData[1];
        const runTime = moment(currentFcstRun.runtime);
        const formattedRunTime = runTime.clone().utc().format('YYYYMMDDHHmm');
        const limit = latestRadarDtg
          .clone()
          .add(this.forecastLookaheadMinutes, 'minutes');

        const fcstDtgs = [...currentFcstRun.validAt].sort(
          (a, b) => moment(a.dtg).valueOf() - moment(b.dtg).valueOf()
        );

        const frames: RadarDtg[] = [];
        for (let i = 0; i < fcstDtgs.length; i++) {
          const dtg = moment(fcstDtgs[i].dtg);

          if (dtg.isAfter(limit)) {
            break;
          }

          frames.push({
            mapServer: formattedRunTime,
            info: dtg.clone().format('dd DD.MM.YYYY HH:mm'),
            slider: dtg.unix(),
            visible: false,
            isFcst: true,
            isArchive: false,
            issue: i,
          });
        }

        return frames;
      }),
      catchError(() => of([]))
    );
  }

  setRadarCircleStyle(): void {
    this.radarCirclePaint = {
      'line-color':
        this.mapService.getActiveMapLayer().name === 'street'
          ? 'rgba(88, 88, 88, 1)'
          : 'white',
      'line-width': 1,
    };
  }

  paintCircles(): void {
    this.setRadarCircleStyle();
    if (this.radarZoomLevel() > 4) {
      const settings = this.settings();
      const circles = this.getCircles([
        settings['zoomPoint']['longitude'],
        settings['zoomPoint']['latitude'],
      ]);
      this.radarCircleGeometry.set([]);
      for (const circle of circles) {
        this.radarCircleGeometry.update(radarCircleGeometry => {
          return [
            ...radarCircleGeometry,
            {
              type: 'LineString',
              coordinates: circle,
            },
          ];
        });
      }
    }
  }

  getCircles(point: LngLatLike): Array<LngLatLike> {
    // Source: https://stackoverflow.com/questions/16942697/geojson-circles-supported-or-not
    const x =
      Math.log(Math.tan(((90 + point[1]) * Math.PI) / 360)) / (Math.PI / 180);
    const y = point[0];
    const circles = [];
    for (const r of [0.15, 0.75, 1.5]) {
      const coordinates = [];
      for (let i = 0; i <= 360; i++) {
        const angle = (i * Math.PI) / 180;
        let ptx = x + r * Math.cos(angle);
        const pty = y + r * Math.sin(angle);
        ptx =
          (180 / Math.PI) *
          (2 * Math.atan(Math.exp((ptx * Math.PI) / 180)) - Math.PI / 2);
        const coordinate: LngLatLike = [pty, ptx];
        coordinates.push(coordinate);
      }
      circles.push(coordinates);
    }
    return circles;
  }
}
